/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { INPUT_STYLE_TYPES, INPUT_TYPES } from 'CommonConstants';

import InputClearSVG from '@Assets/Svgs/ic_input_clear.svg';
import TX from '../Text';
import TextCSS from '../Text/Styles';

const Input = ({
  isShowClearButton,
  disabled,
  inputRef,
  inputStyleType,
  type,
  maxLength,
  placeholder,
  width,
  height,
  containerStyle,
  inputStyle,
  value,
  onChange,
  onBlur,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <InputBlock style={containerStyle} inputStyleType={inputStyleType} width={width}>
      <StyledInput
        autoComplete="on"
        isShowClearButton={isShowClearButton}
        isFocused={isFocused}
        disabled={disabled}
        ref={inputRef}
        inputStyleType={inputStyleType}
        type={type}
        maxLength={maxLength || undefined}
        placeholder={placeholder}
        value={value}
        height={height}
        style={inputStyle}
        onChange={event => {
          const { value: targetValue } = event.target;
          if (maxLength && targetValue.length > maxLength) {
            return;
          }
          onChange(event);
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          onBlur();
          setIsFocused(false);
        }}
        {...props}
      />

      {inputStyleType !== INPUT_STYLE_TYPES.FILLED && (
        <InputRightWrapper>
          {isShowClearButton && isFocused && value.length > 0 && (
            <InputClearButton onMouseDown={() => onChange({ target: { value: '' } })}>
              <InputClearIcon />
            </InputClearButton>
          )}

          {maxLength && isFocused ? (
            <InputValueLengthWrapper>
              <InputValueLength>
                {value.length}/{maxLength}
              </InputValueLength>
            </InputValueLengthWrapper>
          ) : null}
        </InputRightWrapper>
      )}
    </InputBlock>
  );
};

Input.propTypes = {
  isShowClearButton: PropTypes.bool,
  disabled: PropTypes.bool,
  inputRef: PropTypes.shape({
    current: PropTypes.oneOfType([PropTypes.bool, PropTypes.any]),
  }),
  inputStyleType: PropTypes.string,
  type: PropTypes.oneOf([INPUT_TYPES.TEXT, INPUT_TYPES.NUMBER]),
  maxLength: PropTypes.number,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  containerStyle: PropTypes.shape({}),
  inputStyle: PropTypes.shape({}),
  width: PropTypes.number,
  height: PropTypes.number,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
};

Input.defaultProps = {
  isShowClearButton: true,
  disabled: false,
  inputRef: undefined,
  inputStyleType: INPUT_STYLE_TYPES.DEFAULT,
  type: INPUT_TYPES.TEXT,
  maxLength: 0,
  placeholder: 'placeholder',
  value: '',
  containerStyle: {},
  inputStyle: {},
  width: 200,
  height: 38,
  onChange: () => {},
  onBlur: () => {},
};

const InputBlock = styled.div`
  position: relative;
  width: ${props => props.width}px;

  ${({ inputStyleType, theme }) =>
    inputStyleType === INPUT_STYLE_TYPES.OUTLINED &&
    css`
      border: 1px solid ${theme.colors.ui_4};
      border-radius: 9px;
    `};
`;

const InputCSS = css`
  ${TextCSS.BodyM}

  width: 100%;
  height: ${props => props.height}px;
  border-radius: 9px;
  border: 0;
  padding: 8px;
  margin-top: 1px;
  padding-right: ${props => (props.isFocused ? 74 : 8)}px;
  vertical-align: middle;

  &:focus {
    outline: none;
    caret-color: ${({ theme }) => theme.colors.ui_on_2};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.ui_on_1};
    opacity: 0.3;
  }
`;

const StyledInput = styled.input`
  ${InputCSS}

  ${({ inputStyleType }) =>
    inputStyleType === INPUT_STYLE_TYPES.FILLED &&
    css`
      ${TextCSS.FootnoteM}

      border-radius: 7px;
      background-color: ${({ theme }) => theme.colors.ui_2};
      padding-right: 0px;
      padding: 5px 7px;

      &::placeholder {
        color: ${({ theme }) => theme.colors.ui_on_4};
        opacity: 1;
      }
    `}
`;

const InputRightWrapper = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  top: 1px;
  right: 7px;
`;

const FlexAlignCenterDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const InputClearButton = styled(FlexAlignCenterDiv)`
  width: 32px;
  height: 32px;
`;

const InputClearIcon = styled.img.attrs({ src: InputClearSVG, alt: '전체 삭제' })`
  width: 16px;
  height: 16px;

  cursor: pointer;
`;

const InputValueLengthWrapper = styled(FlexAlignCenterDiv)`
  justify-content: flex-end;

  padding-right: 4px;
  padding-bottom: 2px;
`;

const InputValueLength = styled(TX.Caption1)`
  color: ${({ theme }) => theme.colors.ui_on_4};
  height: 38px;
`;

export default Input;
